import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Mail, Menu as MenuIcon, X as XIcon, Send,
  CheckCircle2, AlertTriangle, Music, FileText, ShieldCheck,
  ClipboardList, Sparkles,
} from "lucide-react";
import { publicApi, errMsg } from "@/lib/api";

// ---------------------------------------------------------------------------
// Kandake Compliance Services — Public one-page site (MP7)
//
// Premium, restrained, music-industry-fluent. No client login, no uploads, no
// public catalog, no dashboards. A consultation-request doorway only.
//
// Visual character: discreet high-end music rights and compliance firm.
// Palette: ink/charcoal/burgundy/antique-gold/warm-cream.
// Typography: Playfair Display (headlines) + Figtree (body), injected at mount.
// Motion: lightweight CSS only. Respects prefers-reduced-motion.
// ---------------------------------------------------------------------------

// Brand tokens — kept inline so this page does not couple to the global
// design tokens used by the authenticated app. Consistent in spirit with
// /gfmp and /d49.
const INK     = "hsl(20 14% 6%)";   // primary near-black ground
const INK_2   = "hsl(20 12% 9%)";   // slightly lifted layered surface
const CHAR    = "hsl(20 10% 16%)";  // muted charcoal
const CREAM   = "hsl(36 33% 96%)";  // warm cream contrast
const CREAM_2 = "hsl(36 22% 93%)";  // cream tint
const BURG    = "hsl(347 60% 22%)"; // deep burgundy
const GOLD    = "hsl(40 48% 58%)";  // antique gold accent

const KANDAKE_LOGO = "/brand/kandake-compliance.png";

const FONT_LINK_HREF =
  "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@500;600;700&family=Figtree:wght@300;400;500;600&display=swap";

// ----- Catalog size + Primary need fallbacks (server provides canonical)
const CATALOG_SIZE_FALLBACK = [
  "1\u20135 works",
  "6\u201320 works",
  "21\u201350 works",
  "51\u2013100 works",
  "More than 100 works",
  "Not sure yet",
];
const PRIMARY_NEED_FALLBACK = [
  "Catalog assessment",
  "Metadata and split review",
  "Rights documentation",
  "Registration readiness",
  "Sync readiness",
  "I am not sure",
];

const BLANK_FORM = {
  full_name: "",
  email: "",
  phone: "",
  org_name: "",
  catalog_size: "",
  primary_need: "",
  description: "",
  website: "",          // honeypot — must remain empty for legit users
};

const NAV_ITEMS = [
  { href: "#what-we-do",          label: "What We Do" },
  { href: "#who-we-serve",        label: "Who We Serve" },
  { href: "#how-it-works",        label: "How It Works" },
  { href: "#request-consultation",label: "Request a Consultation" },
];

// ===========================================================================
// Shared, tiny styled primitives. Defined inline so this page is single-file.
// ===========================================================================
function GoldRule({ width = 48, style }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width,
        height: 1,
        background: GOLD,
        verticalAlign: "middle",
        ...style,
      }}
    />
  );
}

function Eyebrow({ children, light, dataTestId }) {
  return (
    <div
      className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em]"
      style={{ color: light ? `${CREAM}b0` : `${BURG}cc` }}
      data-testid={dataTestId}
    >
      <GoldRule width={20} />
      {children}
    </div>
  );
}

function PrimaryButton({ href, onClick, children, dataTestId, size = "md" }) {
  const sz = size === "lg"
    ? "px-6 py-3 text-[12.5px]"
    : "px-5 py-2.5 text-[12px]";
  const inner = (
    <span
      className={`inline-flex items-center gap-2 ${sz} rounded-full uppercase tracking-[0.22em] transition-opacity hover:opacity-90`}
      style={{ background: GOLD, color: INK, border: `1px solid ${GOLD}` }}
      data-testid={dataTestId}
    >
      {children}
    </span>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="focus:outline-none">
        {inner}
      </button>
    );
  }
  return <a href={href} className="focus:outline-none">{inner}</a>;
}

function GhostButton({ href, onClick, children, dataTestId, light }) {
  const inner = (
    <span
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[12px] uppercase tracking-[0.22em] transition-opacity hover:opacity-95"
      style={{
        background: "transparent",
        color: light ? CREAM : INK,
        border: `1px solid ${light ? `${GOLD}66` : `${INK}66`}`,
      }}
      data-testid={dataTestId}
    >
      {children}
    </span>
  );
  if (onClick) {
    return (
      <button type="button" onClick={onClick} className="focus:outline-none">
        {inner}
      </button>
    );
  }
  return <a href={href} className="focus:outline-none">{inner}</a>;
}

// ===========================================================================
// Cinematic opening sequence — overlay shown for ~2.4s on first mount.
// Respects prefers-reduced-motion: when set, the overlay is suppressed
// entirely and the page renders immediately.
// ===========================================================================
function CinematicOpener({ onDone, prefersReducedMotion }) {
  const [hide, setHide] = useState(false);
  useEffect(() => {
    if (prefersReducedMotion) {
      onDone && onDone();
      return;
    }
    const t1 = setTimeout(() => setHide(true), 2200);
    const t2 = setTimeout(() => onDone && onDone(), 2700);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [onDone, prefersReducedMotion]);

  if (prefersReducedMotion) return null;

  return (
    <div
      aria-hidden="true"
      data-testid="kandake-cinematic-overlay"
      style={{
        position: "fixed",
        inset: 0,
        background: INK,
        zIndex: 100,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: hide ? 0 : 1,
        transition: "opacity 480ms ease-in-out",
        pointerEvents: hide ? "none" : "auto",
      }}
    >
      {/* Concentric line motif (record grooves / alignment rings) */}
      <svg
        viewBox="-200 -200 400 400"
        style={{
          position: "absolute",
          width: "min(680px, 90vw)",
          height: "min(680px, 90vw)",
          opacity: 0.85,
        }}
        aria-hidden="true"
      >
        <defs>
          <radialGradient id="rg-fade" cx="50%" cy="50%" r="50%">
            <stop offset="60%" stopColor={GOLD} stopOpacity="0.95" />
            <stop offset="100%" stopColor={GOLD} stopOpacity="0" />
          </radialGradient>
        </defs>
        {[60, 92, 124, 156, 188].map((r, i) => (
          <circle
            key={r}
            cx="0"
            cy="0"
            r={r}
            fill="none"
            stroke={GOLD}
            strokeOpacity={0.6 - i * 0.08}
            strokeWidth={1}
            strokeDasharray={`${4 + i * 3} ${10 + i * 4}`}
            style={{
              transformOrigin: "center",
              animation: `kc-ring-spin ${22 + i * 7}s linear infinite ${i % 2 === 0 ? "" : "reverse"}`,
            }}
          />
        ))}
        {/* Constellation dots */}
        {[
          [-150, -22], [120, 8], [-44, 130], [70, -148], [148, 100], [-130, 96],
        ].map(([x, y], i) => (
          <circle
            key={i}
            cx={x}
            cy={y}
            r={2}
            fill={GOLD}
            opacity={0.85}
            style={{
              animation: `kc-twinkle 3.6s ease-in-out ${i * 0.32}s infinite`,
            }}
          />
        ))}
        {/* Connecting lines */}
        <g stroke={GOLD} strokeOpacity="0.35" strokeWidth="0.75">
          <line x1="-150" y1="-22" x2="120" y2="8"
            style={{ animation: "kc-line-draw 1400ms ease-out 200ms both", strokeDasharray: 320, strokeDashoffset: 320 }} />
          <line x1="120" y1="8" x2="148" y2="100"
            style={{ animation: "kc-line-draw 1400ms ease-out 380ms both", strokeDasharray: 200, strokeDashoffset: 200 }} />
          <line x1="148" y1="100" x2="-44" y2="130"
            style={{ animation: "kc-line-draw 1400ms ease-out 560ms both", strokeDasharray: 220, strokeDashoffset: 220 }} />
          <line x1="-44" y1="130" x2="-130" y2="96"
            style={{ animation: "kc-line-draw 1400ms ease-out 740ms both", strokeDasharray: 120, strokeDashoffset: 120 }} />
          <line x1="-130" y1="96" x2="-150" y2="-22"
            style={{ animation: "kc-line-draw 1400ms ease-out 920ms both", strokeDasharray: 140, strokeDashoffset: 140 }} />
          <line x1="70" y1="-148" x2="-150" y2="-22"
            style={{ animation: "kc-line-draw 1400ms ease-out 1100ms both", strokeDasharray: 260, strokeDashoffset: 260 }} />
        </g>
      </svg>

      {/* Centred Kandake logo, soft fade-in */}
      <img
        src={KANDAKE_LOGO}
        alt="Kandake"
        className="relative"
        style={{
          width: "min(220px, 36vw)",
          height: "auto",
          filter: "drop-shadow(0 8px 30px rgba(0,0,0,0.5))",
          opacity: 0,
          transform: "scale(0.86)",
          animation: "kc-logo-rise 1500ms cubic-bezier(.16,.84,.32,1) 240ms forwards",
        }}
        draggable={false}
      />
    </div>
  );
}

// ===========================================================================
// Static still motif — used by reduced-motion users and as the hero backdrop
// after the cinematic opener completes.
// ===========================================================================
function HeroBackdrop({ reduced }) {
  return (
    <svg
      viewBox="-200 -200 400 400"
      style={{
        position: "absolute",
        inset: 0,
        margin: "auto",
        width: "min(820px, 95vw)",
        height: "min(820px, 95vw)",
        opacity: 0.55,
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      {[60, 92, 124, 156, 188].map((r, i) => (
        <circle
          key={r}
          cx="0"
          cy="0"
          r={r}
          fill="none"
          stroke={GOLD}
          strokeOpacity={0.42 - i * 0.06}
          strokeWidth={1}
          strokeDasharray={`${4 + i * 3} ${10 + i * 4}`}
          style={!reduced ? {
            transformOrigin: "center",
            animation: `kc-ring-spin ${40 + i * 9}s linear infinite ${i % 2 === 0 ? "" : "reverse"}`,
          } : undefined}
        />
      ))}
    </svg>
  );
}

// ===========================================================================
// Header — sticky, restrained. Mobile menu uses a sheet-style dropdown.
// ===========================================================================
function SiteHeader({ visible }) {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="kandake-header"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        background: INK + "ee",
        backdropFilter: "saturate(120%) blur(8px)",
        borderBottom: `1px solid ${GOLD}26`,
        opacity: visible ? 1 : 0,
        transition: "opacity 500ms ease 80ms",
      }}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-3 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-3 group" data-testid="kandake-header-logo">
          <img
            src={KANDAKE_LOGO}
            alt="Kandake Compliance Services"
            style={{ height: 38, width: "auto" }}
            draggable={false}
          />
          <span
            className="hidden sm:flex flex-col leading-tight"
            style={{ color: CREAM }}
          >
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: 16, letterSpacing: "0.02em" }}>
              Kandake
            </span>
            <span
              style={{
                fontSize: 9,
                letterSpacing: "0.24em",
                textTransform: "uppercase",
                color: `${GOLD}cc`,
              }}
            >
              Compliance Services
            </span>
          </span>
        </a>

        <nav
          className="hidden md:flex items-center gap-6"
          aria-label="Primary"
          data-testid="kandake-nav-desktop"
        >
          {NAV_ITEMS.slice(0, 3).map((i) => (
            <a
              key={i.href}
              href={i.href}
              className="text-[11px] uppercase tracking-[0.2em] hover:opacity-100 transition-opacity"
              style={{ color: `${CREAM}cc` }}
              data-testid={`kandake-nav-${i.href.slice(1)}`}
            >
              {i.label}
            </a>
          ))}
          <a
            href="#request-consultation"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] uppercase tracking-[0.22em] transition-opacity hover:opacity-90"
            style={{ background: GOLD, color: INK }}
            data-testid="kandake-nav-cta"
          >
            Request a Consultation
          </a>
        </nav>

        <button
          type="button"
          className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-full"
          style={{ color: CREAM, border: `1px solid ${GOLD}55` }}
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls="kandake-mobile-menu"
          aria-label={open ? "Close menu" : "Open menu"}
          data-testid="kandake-menu-toggle"
        >
          {open ? <XIcon className="w-4 h-4" /> : <MenuIcon className="w-4 h-4" />}
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div
          id="kandake-mobile-menu"
          data-testid="kandake-mobile-menu"
          className="md:hidden"
          style={{ background: INK_2, borderTop: `1px solid ${GOLD}26` }}
        >
          <nav className="max-w-6xl mx-auto px-6 py-4 flex flex-col gap-3" aria-label="Mobile">
            {NAV_ITEMS.map((i) => (
              <a
                key={i.href}
                href={i.href}
                onClick={() => setOpen(false)}
                className="text-[13px] uppercase tracking-[0.2em] py-2"
                style={{ color: i.href === "#request-consultation" ? GOLD : `${CREAM}cc` }}
                data-testid={`kandake-mobile-nav-${i.href.slice(1)}`}
              >
                {i.label}
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

// ===========================================================================
// Hero
// ===========================================================================
function Hero({ visible, prefersReducedMotion }) {
  return (
    <section
      id="top"
      data-testid="kandake-hero"
      style={{ background: INK, color: CREAM, position: "relative", overflow: "hidden" }}
    >
      <HeroBackdrop reduced={prefersReducedMotion} />
      <div
        className="relative max-w-5xl mx-auto px-6 sm:px-10 py-20 sm:py-28 text-center"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0)" : "translateY(12px)",
          transition: "opacity 700ms ease 200ms, transform 700ms ease 200ms",
        }}
      >
        <Eyebrow light dataTestId="kandake-hero-eyebrow">Kandake Compliance Services</Eyebrow>
        <h1
          className="mt-5 leading-[1.05]"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(34px, 6vw, 60px)",
            fontWeight: 600,
            letterSpacing: "-0.01em",
            color: CREAM,
          }}
          data-testid="kandake-hero-headline"
        >
          Catalog Compliance{" "}
          <span style={{ color: GOLD, fontStyle: "italic" }}>&amp;</span>{" "}
          Sync Readiness
        </h1>
        <p
          className="mt-6 max-w-2xl mx-auto leading-relaxed"
          style={{ color: `${CREAM}c9`, fontSize: "clamp(15px, 1.4vw, 17px)" }}
          data-testid="kandake-hero-tagline"
        >
          Great music gets attention. <span style={{ color: GOLD }}>Cleared music gets you paid.</span>
        </p>
        <p
          className="mt-5 max-w-2xl mx-auto leading-relaxed"
          style={{ color: `${CREAM}9c`, fontSize: "14px" }}
          data-testid="kandake-hero-copy"
        >
          Great music deserves a clear path to opportunity. Kandake helps artists, producers, labels,
          publishers, estates, and catalog owners organize the rights, documentation, metadata, and
          readiness work that supports responsible administration, licensing conversations, and
          future opportunity.
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <PrimaryButton href="#request-consultation" dataTestId="kandake-hero-cta-primary" size="lg">
            Request a Consultation
          </PrimaryButton>
          <GhostButton href="#how-it-works" dataTestId="kandake-hero-cta-secondary" light>
            See How It Works
          </GhostButton>
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// Problem
// ===========================================================================
function ProblemSection() {
  return (
    <section
      data-testid="kandake-problem"
      style={{ background: CREAM, color: INK, position: "relative", overflow: "hidden" }}
    >
      {/* Abstract fragmented-lines-aligning motif (no literal paperwork) */}
      <svg
        viewBox="0 0 1200 320"
        preserveAspectRatio="none"
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity: 0.5 }}
        aria-hidden="true"
      >
        {Array.from({ length: 26 }).map((_, i) => {
          const baseY = 30 + i * 11;
          const wobble = ((i * 13) % 19) - 9;
          return (
            <line
              key={i}
              x1={0}
              y1={baseY + (i < 13 ? wobble : 0)}
              x2={1200}
              y2={baseY}
              stroke={GOLD}
              strokeOpacity={0.16 + (i / 26) * 0.18}
              strokeWidth={i % 5 === 0 ? 1 : 0.6}
            />
          );
        })}
      </svg>
      <div className="relative max-w-4xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
        <Eyebrow dataTestId="kandake-problem-eyebrow">The Reality</Eyebrow>
        <h2
          className="mt-4 leading-tight"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(28px, 4.2vw, 40px)",
            fontWeight: 600,
            color: INK,
            letterSpacing: "-0.005em",
          }}
          data-testid="kandake-problem-headline"
        >
          Before music can move, the paperwork has to be ready.
        </h2>
        <p
          className="mt-6 max-w-2xl leading-relaxed"
          style={{ color: `${INK}c9`, fontSize: 15 }}
          data-testid="kandake-problem-copy"
        >
          A strong song or master recording can still be difficult to administer, license, distribute,
          or pitch when ownership details, split information, registrations, title records,
          agreements, and supporting documentation are incomplete or unclear.
        </p>
        <p
          className="mt-4 max-w-2xl leading-relaxed"
          style={{ color: `${BURG}`, fontSize: 14.5, fontWeight: 500 }}
        >
          Kandake provides a structured starting point for bringing catalog information into order.
        </p>
      </div>
    </section>
  );
}

// ===========================================================================
// What We Do — five service blocks
// ===========================================================================
const SERVICES = [
  {
    icon: ClipboardList,
    title: "Catalog Assessment",
    body: "A practical review of what is available, what is missing, and what needs attention before a catalog moves forward.",
  },
  {
    icon: Music,
    title: "Metadata & Split Review",
    body: "Organization and review of titles, writers, publishers, ownership percentages, contributors, and related metadata.",
  },
  {
    icon: FileText,
    title: "Rights Documentation Readiness",
    body: "Identification and organization of the agreements, ownership records, permissions, and supporting documents connected to a work or recording.",
  },
  {
    icon: ShieldCheck,
    title: "Registration-Readiness Guidance",
    body: "Structured preparation for the information commonly needed before rights registrations and administrative filings can be completed.",
  },
  {
    icon: Sparkles,
    title: "Sync-Readiness Preparation",
    body: "Preparation of catalog information and documentation so that music is better positioned for future licensing conversations and opportunity.",
  },
];

function WhatWeDoSection() {
  return (
    <section
      id="what-we-do"
      data-testid="kandake-what-we-do"
      style={{ background: INK, color: CREAM }}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
        <div className="max-w-3xl">
          <Eyebrow light dataTestId="kandake-services-eyebrow">What We Do</Eyebrow>
          <h2
            className="mt-4 leading-tight"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(28px, 4.2vw, 42px)",
              fontWeight: 600,
              color: CREAM,
              letterSpacing: "-0.005em",
            }}
            data-testid="kandake-services-headline"
          >
            What Kandake helps you organize.
          </h2>
        </div>

        <ol
          className="mt-12 grid gap-px"
          style={{
            background: `${GOLD}22`,
            border: `1px solid ${GOLD}1a`,
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          }}
          data-testid="kandake-services-grid"
        >
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <li
                key={s.title}
                className="p-7 sm:p-8 flex flex-col gap-3"
                style={{ background: INK_2 }}
                data-testid={`kandake-service-${i + 1}`}
              >
                <div
                  className="inline-flex items-center justify-center"
                  style={{
                    width: 36, height: 36, borderRadius: 999,
                    background: `${GOLD}1a`, border: `1px solid ${GOLD}55`,
                    color: GOLD,
                  }}
                  aria-hidden="true"
                >
                  <Icon className="w-4 h-4" />
                </div>
                <div
                  className="text-[10px] uppercase tracking-[0.22em]"
                  style={{ color: `${GOLD}cc` }}
                >
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h3
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontSize: 22,
                    fontWeight: 600,
                    color: CREAM,
                    letterSpacing: "-0.005em",
                  }}
                >
                  {s.title}
                </h3>
                <p
                  className="leading-relaxed"
                  style={{ color: `${CREAM}b3`, fontSize: 14 }}
                >
                  {s.body}
                </p>
              </li>
            );
          })}
        </ol>

        <p
          className="mt-10 text-center"
          style={{ color: `${CREAM}a8`, fontSize: 13.5, fontStyle: "italic" }}
          data-testid="kandake-services-tailored-note"
        >
          Services are tailored to the condition and needs of each catalog.
        </p>
      </div>
    </section>
  );
}

// ===========================================================================
// Who We Serve — editorial column list
// ===========================================================================
const AUDIENCES = [
  "Independent artists",
  "Producers and writer-producers",
  "Labels",
  "Publishers",
  "Estates and heirs",
  "Legacy catalog owners",
  "Music creators preparing for release, administration, licensing, or transition",
];

function WhoWeServeSection() {
  return (
    <section
      id="who-we-serve"
      data-testid="kandake-who-we-serve"
      style={{ background: CREAM_2, color: INK }}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-24 grid lg:grid-cols-[1.05fr_1fr] gap-12">
        <div>
          <Eyebrow dataTestId="kandake-audience-eyebrow">Who We Serve</Eyebrow>
          <h2
            className="mt-4 leading-tight"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: "clamp(28px, 4.2vw, 40px)",
              fontWeight: 600,
              color: INK,
              letterSpacing: "-0.005em",
            }}
            data-testid="kandake-audience-headline"
          >
            Built for music that has a future.
          </h2>
          <p
            className="mt-6 max-w-md leading-relaxed"
            style={{ color: `${INK}b3`, fontSize: 14.5 }}
            data-testid="kandake-audience-copy"
          >
            Whether the catalog is one song, one project, or years of recorded work, Kandake begins
            by helping identify what is in place and what still needs to be resolved.
          </p>
        </div>
        <ul
          className="grid gap-px"
          style={{ background: `${INK}1a`, border: `1px solid ${INK}14` }}
          data-testid="kandake-audience-list"
        >
          {AUDIENCES.map((a, i) => (
            <li
              key={a}
              className="px-5 py-4 flex items-start gap-3"
              style={{ background: CREAM }}
              data-testid={`kandake-audience-${i + 1}`}
            >
              <span
                aria-hidden="true"
                className="mt-2.5 block w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: GOLD }}
              />
              <span style={{ color: INK, fontSize: 14.5 }}>{a}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

// ===========================================================================
// How It Works — 3 steps + disclaimer
// ===========================================================================
const STEPS = [
  {
    title: "Request a Consultation",
    body: "Tell us a little about your music, catalog, or current needs.",
  },
  {
    title: "Initial Review",
    body: "We identify the general condition of the materials and determine the appropriate next conversation.",
  },
  {
    title: "Build the Readiness Path",
    body: "Where appropriate, Kandake outlines the documentation, information, and workflow needed to bring the catalog toward a more organized and usable condition.",
  },
];

function HowItWorksSection() {
  return (
    <section
      id="how-it-works"
      data-testid="kandake-how-it-works"
      style={{ background: CHAR, color: CREAM }}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
        <Eyebrow light dataTestId="kandake-hiw-eyebrow">How It Works</Eyebrow>
        <h2
          className="mt-4 leading-tight"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(28px, 4.2vw, 40px)",
            fontWeight: 600,
            color: CREAM,
            letterSpacing: "-0.005em",
          }}
          data-testid="kandake-hiw-headline"
        >
          A clear starting point.
        </h2>

        <ol
          className="mt-12 grid gap-6 sm:gap-8 md:grid-cols-3"
          data-testid="kandake-steps-grid"
        >
          {STEPS.map((s, i) => (
            <li
              key={s.title}
              className="relative pl-10"
              data-testid={`kandake-step-${i + 1}`}
            >
              <span
                aria-hidden="true"
                className="absolute left-0 top-1"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 30,
                  fontStyle: "italic",
                  color: GOLD,
                  lineHeight: 1,
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 22,
                  fontWeight: 600,
                  color: CREAM,
                  marginBottom: 8,
                }}
              >
                {s.title}
              </h3>
              <p style={{ color: `${CREAM}b3`, fontSize: 14, lineHeight: 1.65 }}>
                {s.body}
              </p>
            </li>
          ))}
        </ol>

        <div
          className="mt-14 max-w-3xl rounded-md px-5 py-4"
          style={{ background: INK_2, border: `1px solid ${GOLD}33`, color: `${CREAM}cc` }}
          role="note"
          data-testid="kandake-legal-disclaimer"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" style={{ color: GOLD }} aria-hidden="true" />
            <p style={{ fontSize: 12.5, lineHeight: 1.6 }}>
              Kandake Compliance Services does not provide legal advice. Where legal review, attorney
              clearance, or specialist services are appropriate, clients may be advised accordingly.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// Consultation form
// ===========================================================================
const INPUT_STYLE = {
  background: INK_2,
  color: CREAM,
  border: `1px solid ${GOLD}33`,
  borderRadius: 6,
  width: "100%",
  padding: "10px 12px",
  fontSize: 14,
  outline: "none",
};
const INPUT_STYLE_INVALID = {
  ...INPUT_STYLE,
  borderColor: BURG,
};

function ConsultationFormSection() {
  const [meta, setMeta] = useState({
    catalog_size_options: CATALOG_SIZE_FALLBACK,
    primary_need_options: PRIMARY_NEED_FALLBACK,
  });
  const [form, setForm] = useState(BLANK_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const mountedAtRef = useRef(Date.now());

  function setField(k, v) {
    setForm((s) => ({ ...s, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  }

  useEffect(() => {
    let cancelled = false;
    publicApi.get("/public/kandake/meta")
      .then((r) => {
        if (cancelled) return;
        setMeta({
          catalog_size_options: Array.isArray(r.data?.catalog_size_options) && r.data.catalog_size_options.length
            ? r.data.catalog_size_options : CATALOG_SIZE_FALLBACK,
          primary_need_options: Array.isArray(r.data?.primary_need_options) && r.data.primary_need_options.length
            ? r.data.primary_need_options : PRIMARY_NEED_FALLBACK,
        });
      })
      .catch(() => { /* fall back silently */ });
    return () => { cancelled = true; };
  }, []);

  function validate() {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please tell us your name.";
    if (!form.email.trim()) e.email = "Please provide an email address.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = "Please use a valid email address.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function submit(ev) {
    ev.preventDefault();
    setSubmitError("");
    if (!validate()) {
      // Move focus to the first invalid field for keyboard users.
      const first = Object.keys(errors)[0];
      if (first) {
        const el = document.getElementById(`kc-${first}`);
        if (el && typeof el.focus === "function") el.focus();
      }
      return;
    }
    setSubmitting(true);
    try {
      const elapsed = Math.max(0, Date.now() - mountedAtRef.current);
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        org_name: form.org_name.trim(),
        catalog_size: form.catalog_size.trim(),
        primary_need: form.primary_need.trim(),
        description: form.description.trim(),
        website: form.website,               // honeypot pass-through
        form_elapsed_ms: elapsed,
      };
      const r = await publicApi.post("/public/kandake/consultation", payload);
      setSuccess(r.data);
      setForm(BLANK_FORM);
      // Reset the timer so any subsequent submission also counts elapsed
      // from the moment the form is re-opened.
      mountedAtRef.current = Date.now();
    } catch (e) {
      setSubmitError(errMsg(e, "Unable to submit your request. Please try again."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section
      id="request-consultation"
      data-testid="kandake-consultation"
      style={{ background: INK, color: CREAM, position: "relative" }}
    >
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
        <Eyebrow light dataTestId="kandake-consultation-eyebrow">Begin</Eyebrow>
        <h2
          className="mt-4 leading-tight"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "clamp(28px, 4.2vw, 40px)",
            fontWeight: 600,
            color: CREAM,
            letterSpacing: "-0.005em",
          }}
          data-testid="kandake-consultation-headline"
        >
          Start with a conversation.
        </h2>
        <p
          className="mt-5 max-w-2xl leading-relaxed"
          style={{ color: `${CREAM}b3`, fontSize: 14.5 }}
          data-testid="kandake-consultation-copy"
        >
          Tell us a little about the catalog or project you need help organizing. We will review the
          request and follow up regarding the appropriate next step.
        </p>

        {success ? (
          <div
            className="mt-10 rounded-2xl px-6 sm:px-8 py-7"
            data-testid="kandake-consultation-success"
            style={{ background: INK_2, border: `1px solid ${GOLD}44` }}
            role="status"
          >
            <CheckCircle2 className="w-7 h-7 mb-3" style={{ color: GOLD }} aria-hidden="true" />
            <div
              className="text-[10px] uppercase tracking-[0.22em] mb-1"
              style={{ color: `${GOLD}cc` }}
            >
              Received
            </div>
            <h3
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 22,
                fontWeight: 600,
                color: CREAM,
                lineHeight: 1.2,
              }}
            >
              Thank you. Your consultation request has been received.
            </h3>
            <p
              className="mt-4 leading-relaxed"
              style={{ color: `${CREAM}cc`, fontSize: 14 }}
              data-testid="kandake-consultation-success-copy"
            >
              Kandake Compliance Services will review the information and follow up regarding the
              appropriate next step.
            </p>
            <button
              type="button"
              onClick={() => {
                setSuccess(null);
                setForm(BLANK_FORM);
                mountedAtRef.current = Date.now();
              }}
              className="mt-7 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] px-4 py-2 rounded-full transition-opacity hover:opacity-90"
              style={{ background: "transparent", color: CREAM, border: `1px solid ${GOLD}66` }}
              data-testid="kandake-consultation-success-new"
            >
              Submit another request
            </button>
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="mt-10 space-y-5"
            data-testid="kandake-consultation-form"
            noValidate
          >
            {submitError && (
              <div
                role="alert"
                className="rounded-md px-4 py-3 text-[13px]"
                style={{ background: `${BURG}33`, border: `1px solid ${BURG}88`, color: CREAM }}
                data-testid="kandake-consultation-error"
              >
                {submitError}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <FormField
                id="full_name" label="Full name" required
                value={form.full_name} onChange={(v) => setField("full_name", v)}
                autoComplete="name" maxLength={160}
                error={errors.full_name}
                dataTestId="kandake-consultation-name"
              />
              <FormField
                id="email" label="Email address" required type="email"
                value={form.email} onChange={(v) => setField("email", v)}
                autoComplete="email" maxLength={200}
                error={errors.email}
                dataTestId="kandake-consultation-email"
              />
              <FormField
                id="phone" label="Phone number (optional)" type="tel"
                value={form.phone} onChange={(v) => setField("phone", v)}
                autoComplete="tel" maxLength={60}
                dataTestId="kandake-consultation-phone"
              />
              <FormField
                id="org_name" label="Artist, company, label, or publisher name"
                value={form.org_name} onChange={(v) => setField("org_name", v)}
                autoComplete="organization" maxLength={200}
                dataTestId="kandake-consultation-org"
              />
              <FormSelect
                id="catalog_size" label="Approximate catalog size"
                value={form.catalog_size} onChange={(v) => setField("catalog_size", v)}
                options={meta.catalog_size_options}
                dataTestId="kandake-consultation-size"
              />
              <FormSelect
                id="primary_need" label="Primary need"
                value={form.primary_need} onChange={(v) => setField("primary_need", v)}
                options={meta.primary_need_options}
                dataTestId="kandake-consultation-need"
              />
            </div>

            <FormTextarea
              id="description" label="Brief description of the need"
              value={form.description} onChange={(v) => setField("description", v)}
              maxLength={4000}
              rows={5}
              placeholder="Anything you'd like Kandake to know about the catalog or project — context, urgency, goals."
              dataTestId="kandake-consultation-desc"
            />

            {/* Hidden honeypot — must remain empty for legit users. */}
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                left: "-9999px",
                width: 1,
                height: 1,
                overflow: "hidden",
              }}
            >
              <label htmlFor="kc-website">Website (do not fill)</label>
              <input
                id="kc-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setField("website", e.target.value)}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4 pt-1">
              <p
                style={{ color: `${CREAM}99`, fontSize: 11.5, lineHeight: 1.55, maxWidth: 440 }}
              >
                Please do not include passwords, banking information, login credentials, or
                unpublished music files. Submissions are reviewed by Kandake Compliance Services
                only.
              </p>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-[12px] uppercase tracking-[0.22em] transition-opacity hover:opacity-90 disabled:opacity-50"
                style={{ background: GOLD, color: INK, border: `1px solid ${GOLD}` }}
                data-testid="kandake-consultation-submit"
              >
                <Send className="w-3.5 h-3.5" />
                {submitting ? "Submitting\u2026" : "Request a Consultation"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function FormLabel({ htmlFor, required, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[10.5px] uppercase tracking-[0.2em] mb-1.5"
      style={{ color: `${GOLD}cc` }}
    >
      {children}
      {required && <span style={{ color: `${BURG}`, marginLeft: 4 }} aria-hidden="true">*</span>}
    </label>
  );
}

function FieldError({ id, message }) {
  if (!message) return null;
  return (
    <p
      id={id}
      className="mt-1 text-[11.5px]"
      style={{ color: `${BURG}`, fontWeight: 500 }}
      role="alert"
    >
      {message}
    </p>
  );
}

function FormField({
  id, label, required, type = "text", value, onChange,
  autoComplete, maxLength, error, dataTestId,
}) {
  const errId = `${id}-error`;
  return (
    <div>
      <FormLabel htmlFor={`kc-${id}`} required={required}>{label}</FormLabel>
      <input
        id={`kc-${id}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        maxLength={maxLength}
        required={required}
        aria-invalid={!!error}
        aria-describedby={error ? errId : undefined}
        style={error ? INPUT_STYLE_INVALID : INPUT_STYLE}
        data-testid={dataTestId}
      />
      <FieldError id={errId} message={error} />
    </div>
  );
}

function FormSelect({ id, label, value, onChange, options, dataTestId }) {
  return (
    <div>
      <FormLabel htmlFor={`kc-${id}`}>{label}</FormLabel>
      <select
        id={`kc-${id}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={INPUT_STYLE}
        data-testid={dataTestId}
      >
        <option value="">Select\u2026</option>
        {options.map((o) => (
          <option key={o} value={o} style={{ color: "#111", background: CREAM }}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function FormTextarea({ id, label, value, onChange, rows = 4, placeholder, maxLength, dataTestId }) {
  return (
    <div>
      <FormLabel htmlFor={`kc-${id}`}>{label}</FormLabel>
      <textarea
        id={`kc-${id}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        maxLength={maxLength}
        style={{ ...INPUT_STYLE, minHeight: 96, resize: "vertical" }}
        data-testid={dataTestId}
      />
    </div>
  );
}

// ===========================================================================
// Footer
// ===========================================================================
function SiteFooter() {
  return (
    <footer
      data-testid="kandake-footer"
      style={{ background: INK_2, color: `${CREAM}99`, borderTop: `1px solid ${GOLD}26` }}
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-12 grid gap-10 md:grid-cols-[1.4fr_1fr_1fr]">
        <div>
          <img
            src={KANDAKE_LOGO}
            alt="Kandake Compliance Services"
            style={{ height: 56, width: "auto", marginBottom: 14 }}
            draggable={false}
          />
          <div
            style={{ fontFamily: "'Playfair Display', serif", color: CREAM, fontSize: 20, lineHeight: 1.1 }}
          >
            Kandake Compliance Services
          </div>
          <div
            className="mt-1 text-[10px] uppercase tracking-[0.24em]"
            style={{ color: `${GOLD}cc` }}
          >
            A service of The Durban 49 Group, LLC
          </div>
          <p
            className="mt-5 max-w-md leading-relaxed"
            style={{ color: `${CREAM}b3`, fontSize: 13.5 }}
          >
            <span style={{ color: CREAM, fontWeight: 500 }}>Catalog Compliance &amp; Sync Readiness.</span>
            <br />
            Great music gets attention. Cleared music gets you paid.
          </p>
        </div>

        <div>
          <div
            className="text-[10px] uppercase tracking-[0.22em] mb-3"
            style={{ color: `${GOLD}cc` }}
          >
            Explore
          </div>
          <ul className="space-y-2 text-[13px]">
            <li><a href="#what-we-do" className="hover:opacity-100" style={{ color: `${CREAM}cc` }}>What We Do</a></li>
            <li><a href="#who-we-serve" className="hover:opacity-100" style={{ color: `${CREAM}cc` }}>Who We Serve</a></li>
            <li><a href="#how-it-works" className="hover:opacity-100" style={{ color: `${CREAM}cc` }}>How It Works</a></li>
            <li><a href="#request-consultation" className="hover:opacity-100" style={{ color: `${GOLD}` }}>Request a Consultation</a></li>
          </ul>
        </div>

        <div>
          <div
            className="text-[10px] uppercase tracking-[0.22em] mb-3"
            style={{ color: `${GOLD}cc` }}
          >
            Group
          </div>
          <ul className="space-y-2 text-[13px]">
            <li><Link to="/d49" className="hover:opacity-100" style={{ color: `${CREAM}cc` }}>The Durban 49 Group</Link></li>
            <li><Link to="/gfmp" className="hover:opacity-100" style={{ color: `${CREAM}cc` }}>Good Friar Music Publishing</Link></li>
            <li><Link to="/gfmp/sync-catalogue" className="hover:opacity-100" style={{ color: `${CREAM}cc` }}>GFMP Sync Catalogue</Link></li>
            <li>
              <Link
                to="/kandake/submit-demo"
                className="hover:opacity-100"
                style={{ color: `${GOLD}` }}
                data-testid="kandake-footer-submit-demo"
              >
                Submit a Demo for Review
              </Link>
            </li>
          </ul>
          <ul className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-[11px]">
            <li>
              <a href="#" onClick={(e) => e.preventDefault()} className="opacity-70 hover:opacity-100"
                 style={{ color: `${CREAM}99` }} data-testid="kandake-footer-privacy">
                Privacy
              </a>
            </li>
            <li>
              <a href="#" onClick={(e) => e.preventDefault()} className="opacity-70 hover:opacity-100"
                 style={{ color: `${CREAM}99` }} data-testid="kandake-footer-terms">
                Terms
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div
        className="max-w-6xl mx-auto px-6 sm:px-10 pb-6 text-[11px]"
        style={{ color: `${CREAM_2}55` }}
      >
        © {new Date().getFullYear()} The Durban 49 Group, LLC. All rights reserved.
      </div>
    </footer>
  );
}

// ===========================================================================
// Main page
// ===========================================================================
export default function Kandake() {
  const [reveal, setReveal] = useState(false);
  const prefersReducedMotion = useReducedMotionMatch();

  // Inject Playfair Display + Figtree exactly once when this page is mounted.
  // Removed on unmount so other pages don't carry the extra fonts.
  useEffect(() => {
    const id = "kandake-font-link";
    if (document.getElementById(id)) return undefined;
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = FONT_LINK_HREF;
    document.head.appendChild(link);
    return () => {
      const existing = document.getElementById(id);
      if (existing) existing.remove();
    };
  }, []);

  // If reduced motion, reveal everything immediately.
  useEffect(() => {
    if (prefersReducedMotion) setReveal(true);
  }, [prefersReducedMotion]);

  return (
    <div
      style={{
        background: INK,
        color: CREAM,
        minHeight: "100vh",
        fontFamily: "'Figtree', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
        // Honest cursor + selection styling for the dark canvas.
      }}
      data-testid="kandake-page"
    >
      <style>{`
        /* Cinematic motion keyframes — local to this page */
        @keyframes kc-ring-spin   { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes kc-logo-rise   { 0% { opacity: 0; transform: scale(0.86); }
                                    60% { opacity: 1; }
                                    100% { opacity: 1; transform: scale(1); } }
        @keyframes kc-twinkle     { 0%, 100% { opacity: 0.2; }
                                    50%      { opacity: 1; } }
        @keyframes kc-line-draw   { to { stroke-dashoffset: 0; } }

        /* Honour reduced motion — disable rotation/twinkle/draw entirely */
        @media (prefers-reduced-motion: reduce) {
          [data-testid="kandake-page"] *,
          [data-testid="kandake-page"] *::before,
          [data-testid="kandake-page"] *::after {
            animation: none !important;
            transition: none !important;
          }
        }
        [data-testid="kandake-page"] ::selection {
          background: ${GOLD};
          color: ${INK};
        }
        [data-testid="kandake-page"] a:focus-visible,
        [data-testid="kandake-page"] button:focus-visible,
        [data-testid="kandake-page"] input:focus-visible,
        [data-testid="kandake-page"] select:focus-visible,
        [data-testid="kandake-page"] textarea:focus-visible {
          outline: 2px solid ${GOLD};
          outline-offset: 2px;
          border-radius: 6px;
        }
        [data-testid="kandake-page"] html {
          scroll-behavior: smooth;
        }
      `}</style>

      <CinematicOpener
        prefersReducedMotion={prefersReducedMotion}
        onDone={() => setReveal(true)}
      />

      <SiteHeader visible={reveal} />
      <main
        style={{
          opacity: reveal ? 1 : 0,
          transition: "opacity 600ms ease",
        }}
      >
        <Hero visible={reveal} prefersReducedMotion={prefersReducedMotion} />
        <ProblemSection />
        <WhatWeDoSection />
        <WhoWeServeSection />
        <HowItWorksSection />
        <ConsultationFormSection />
      </main>
      <SiteFooter />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tiny hook: subscribe to prefers-reduced-motion changes.
// ---------------------------------------------------------------------------
function useReducedMotionMatch() {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = (ev) => setReduced(!!ev.matches);
    // Older Safari uses addListener; modern uses addEventListener.
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return reduced;
}
